<template>
  <div>
    <div>
      <AnalysisSummary
        id="analysis-summary"
        :analysis="currentAnalysisCopy"
        :analysis-id="analysis.id"
        :document-details="documentDetails"
        :on-prepared-analysis="onPreparedAnalysis"
        @selectAnalysis="selectedAnalysis"
      />
    </div>
    <div>
      <ExtensionFooter
        class="extensionFooter"
        :analysis="analysis"
        :on-prepared-analysis="onPreparedAnalysis"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { getDefaultUserAnalysis } from '../../store/analysisHelper';
import AnalysisSummary from './AnalysisSummary';
import ExtensionFooter from './ExtensionFooter';
import { types as analysesTypes } from '../../store/analyses';
import { types as analysesCaptureTypes } from '../../store/analysesCapture';
import { types as userTypes } from '../../store/user';

export default {
  name: 'AnalysisInfo',
  components: {
    AnalysisSummary,
    ExtensionFooter,
  },
  props: {
    analysis: {
      type: Object,
      required: true,
    },
    analysisId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tags: [],
      currentAnalysisCopy: {},
      currentUserAnalysis: undefined,
      currentUsersAnalyses: [],
      documentDetails: {},
    };
  },
  computed: {
    ...mapGetters({
      preparedAnalysis: analysesCaptureTypes.getters.preparedAnalysis,
      onPreparedAnalysis: analysesCaptureTypes.getters.onPreparedAnalysis,
      userDetails: userTypes.getters.userDetails,
      currentAnalysis: analysesTypes.getters.currentAnalysis,
      currentAnalysisUserAnalyses: analysesTypes.getters.currentAnalysisUserAnalyses,
    }),
  },
  methods: {
    ...mapActions({
      fetchAnalysis: analysesTypes.actions.fetchAnalysis,
      resetAnalysis: analysesTypes.actions.resetAnalysis,
      resetPreparedAnalysis: analysesCaptureTypes.actions.resetPreparedAnalysis,
      prepareAnalysis: analysesCaptureTypes.actions.prepareAnalysis,
    }),
    async selectedAnalysis(analysis) {
      if (analysis) {
        if (analysis.id === this.analysis.id && !this.onPreparedAnalysis) {
          return;
        }
      }

      this.currentAnalysisCopy = undefined;

      if (analysis.id) {
        this.resetPreparedAnalysis();
        await this.fetchAnalysis(analysis.id);
      } else {
        await this.resetPreparedAnalysis();
        await this.prepareAnalysis(this.documentDetails);
        await this.setEmptyAnalysis();
      }
      this.$emit('selectAnalysis', analysis);
    },
    async setEmptyAnalysis() {
      this.resetAnalysis();
      this.currentAnalysisCopy = cloneDeep(this.preparedAnalysis);
      this.currentUserAnalysis = getDefaultUserAnalysis({
        userId: this.userDetails.userId,
        displayName: this.userDetails.displayName,
      });

      // TODO: check if trick below is still required
      // Setting to empty and waiting for nextTick before setting back to the desired
      // value in order to trigger the rating reactivity for the current user userAnalysis
      this.currentUsersAnalyses = [];
      await Vue.nextTick();
      this.currentUsersAnalyses = [this.currentUserAnalysis];
    },
    async updateUsersAnalyses() {
      if (!Array.isArray(this.currentAnalysisUserAnalyses)) { return; }

      if (this.currentAnalysisUserAnalyses.length === 0) {
        this.currentUserAnalysis = this.getDefaultCurrentUserAnalysis();
        this.currentUsersAnalyses = [this.currentUserAnalysis];
        return;
      }

      this.currentUserAnalysis = this.currentAnalysisUserAnalyses.find(
        userAnalysis => userAnalysis.id === this.userDetails.userId,
      );

      if (!this.currentUserAnalysis) {
        this.currentUserAnalysis = this.getDefaultCurrentUserAnalysis();
      }

      const otherUserAnalyses = this.currentAnalysisUserAnalyses.filter(userAnalysis =>
        userAnalysis.id !== this.userDetails.userId);

      this.currentUsersAnalyses = [this.currentUserAnalysis];
      otherUserAnalyses.forEach(userAnalysis => this.currentUsersAnalyses.push(userAnalysis));
    },
    async getCurrentAnalysisCopy() {
      if (!this.onPreparedAnalysis && !this.currentAnalysis) {
        throw new Error('Error in state, on null current analysis');
      }

      return cloneDeep(this.currentAnalysis);
    },
  },
  async created() {
    this.documentDetails = this.analysis.documentDetails;
  },
  watch: {
    async currentAnalysis(newVal, oldVal) {
      if (!this.onPreparedAnalysis) {
        this.currentAnalysisCopy = await this.getCurrentAnalysisCopy();
        this.documentDetails = this.currentAnalysisCopy.documentDetails;

        if (oldVal.id !== newVal.id) {
          const newRoute = `/doc/${newVal.documentId}/${newVal.id}`;
          if (newRoute !== this.$route.path) {
            this.$router.push(newRoute);
          }
        }
      }
    },
    async preparedAnalysis() {
      this.documentDetails = this.preparedAnalysis.documentDetails;
    },
    async currentAnalysisUserAnalyses() {
      if (!this.onPreparedAnalysis) {
        this.updateUsersAnalyses();
      }
    },
  },
};
</script>
<style scoped>
.text-label {
  font-weight: 500;
  font-size: 14px;
}
.text-ellipsis {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 12px;
}
.text-container {
  flex-grow: 1;
  min-width: 0;
}
.label-section {
  width: 110px;
  min-width: 110px;
}
.extensionFooter {
  padding: 16px 24px 12px 0px !important;
  margin-right: 12px;
}
</style>
